/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import addProductInformationFilter from "@stoneage/App/Handlers/Documents/addProductInformationFilter";
import removeProductInformationFilter from "@stoneage/App/Handlers/Documents/removeProductInformationFilter";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaResourceCenterSearchEvent,
} from "@stoneage/App/Utilities/DataLayer";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        queryFilter: state.xProductInfo.filters.query,
        pagination: state.xProductInfo.data?.pagination,
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    addProductInformationFilter,
    removeProductInformationFilter,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductInformationSearchQueryStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
    filterTag?: TagPresentationProps;
}

export const productInformationSearchQueryStyles: ProductInformationSearchQueryStyles = {};

const styles = productInformationSearchQueryStyles;

const ProductInformationSearchQuery: FC<Props> = ({
    fields,
    addProductInformationFilter,
    removeProductInformationFilter,
    queryFilter,
    pagination,
    customerNumber,
    impersonation,
}) => {
    const [searchText, setSearchText] = useState("");
    const [isSearchLoading, setIsSearchLoading] = React.useState(false);

    useEffect(() => {
        if (isSearchLoading && pagination && queryFilter) {
            trackGaResourceCenterSearchEvent(queryFilter, pagination.totalItemCount, impersonation, customerNumber);
            setIsSearchLoading(false);
        }
    }, [pagination]);

    const doSearch = () => {
        if (searchText.trim().length !== 0) {
            addProductInformationFilter({ filterType: "Query", value: searchText });
            setIsSearchLoading(true);
        }
        setSearchText("");
    };

    const clickRemoveSearchHandler = () => {
        removeProductInformationFilter({ filterType: "Query", value: "" });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography forwardAs="label" htmlFor="documentsKeywordSearch" {...styles.titleText}>
                {fields.title}
            </Typography>
            <TextField
                {...styles.searchTextField}
                iconClickableProps={{ onClick: doSearch, "aria-roledescription": `Search` }}
                value={searchText}
                onChange={event => setSearchText(event.currentTarget.value)}
                onKeyDown={handleKeyDown}
                placeholder={fields.title}
                type="text"
                data-test-selector="searchInputFilter"
                id="documentsKeywordSearch"
                clickableText="Search"
                aria-multiline="false"
                aria-required="false"
            />

            {queryFilter && (
                <Tag
                    {...styles.filterTag}
                    onDelete={clickRemoveSearchHandler}
                    data-test-selector="searchDocumentsFilter"
                >
                    {translate("Search")}: {queryFilter}
                </Tag>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductInformationSearchQuery),
    definition: {
        group: "Product Information" as any,
        displayName: "Search Within Documents",
        allowedContexts: ["ProductInformationPage"] as any,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "Document Search",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
